import moment, { type Moment } from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getTimeAvailability } from '../../../../../api/AppointmentController';
import AppointmentBookingInternalContext, {
  AppointmentBookingInternalContextType,
} from '../../AppointmentBookingInternalContext';
import type { DateStates } from './types';
import { getTimeSlotNumberFromString } from '../helpers';

const BookingTimeSelectContext =
  React.createContext<BookingTimeSelectContextType>(
    {} as BookingTimeSelectContextType,
  );

export type BookingTimeSelectContextType = {
  appointmentDate: Date;
  setAppointmentDate: React.Dispatch<React.SetStateAction<Date>>;
  /** Minute of the day (Ex: 3:30AM = 210) */
  appointmentTime: number | null;
  setAppointmentTime: React.Dispatch<React.SetStateAction<number | null>>;
  /**
   * Booking dates before this show a no available timeslots error unless the user has access to the listing. A null value indicates all dates are available.
   * Note that this does not consider unavailable logic.
   *  */
  earliestBookingDateAllowed: Date | null;
  selectedDateStates: DateStates;
  setSelectedDateStates: React.Dispatch<React.SetStateAction<DateStates>>;
  isDurationPopoverOpen: boolean;
  setIsDurationPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Key is the minute of the day (Ex: 3:30AM = 210), value is true if that timeslot is unavailable  */
  unavailableTimeSlots: Record<number, true>;
  autoConfirmTimeSlots: Record<number, true>;
  getDateStatesForDate: (date: Moment) => DateStates & {
    isAppointmentDate: boolean;
  };
  onDateClick: (
    options: Omit<DateStates, 'isToday'> & {
      date: Moment;
      ignoreComingSoonWarning?: true;
    },
  ) => void;
  isDisabledDate: (date: Moment) => boolean;
  recordShowingData: {
    isRecordShowing: boolean;
    isModalOpen: boolean;
    /** Once this becomes false it will stay false */
    shouldShowWarning: boolean;
    onOk: () => void;
  };
  setRecordShowingData: React.Dispatch<
    React.SetStateAction<BookingTimeSelectContextType['recordShowingData']>
  >;
  restrictedTimeData: {
    isModalOpen: boolean;
    onCancel: () => void;
    onOk: () => void;
  };
  setRestrictedTimeData: React.Dispatch<
    React.SetStateAction<BookingTimeSelectContextType['restrictedTimeData']>
  >;
  comingSoonWarningData: {
    isModalOpen: boolean;
    onOk: () => void;
  };
  setComingSoonWarningData: React.Dispatch<
    React.SetStateAction<BookingTimeSelectContextType['comingSoonWarningData']>
  >;
  /** Appointment duration in minutes */
  duration: number;
  setDuration: React.Dispatch<React.SetStateAction<number>>;
  selectedTimeStatus: null | 'Restricted' | 'AdvancedAutoconfirm';
  appointmentTimeSlotInfoMap?: {
    [timeSlot: number]:
      | AppointmentBookingInternalContextType['blockingAppointmentList']
      | undefined;
  };
};

// Note that moment().toDate() is used instead of new Date() because moment is using the listing's timezone by default at this point
export const BookingTimeSelectContextContextProvider: React.FC = ({
  children,
}) => {
  const {
    listing,
    offerDetails,
    isAdminWithAccess,
    isListingAgent,
    partialAppointmentConfig,
    setConditionalBuyerAgreementData,
    unavailableDateStrings,
    unavailableAfterDate,
    blockingAppointmentList,
  } = useContext(AppointmentBookingInternalContext);

  const [recordShowingData, setRecordShowingData] = useState<
    BookingTimeSelectContextType['recordShowingData']
  >({
    isRecordShowing: false,
    isModalOpen: false,
    shouldShowWarning: true,
    onOk: () => null,
  });

  const [restrictedTimeData, setRestrictedTimeData] = useState<
    BookingTimeSelectContextType['restrictedTimeData']
  >({
    isModalOpen: false,
    onCancel: () => null,
    onOk: () => null,
  });

  const [comingSoonWarningData, setComingSoonWarningData] = useState<
    BookingTimeSelectContextType['comingSoonWarningData']
  >({
    isModalOpen: false,
    onOk: () => null,
  });

  const [duration, setDuration] = useState(
    Math.min(partialAppointmentConfig!.maxDuration, 60),
  );

  const earliestBookingDateAllowed = useMemo(() => {
    if (listing!.status === 'Coming Soon' && listing!.coming_soon_date) {
      return moment(listing!.coming_soon_date).startOf('day').toDate();
    }
    return moment().startOf('day').toDate();
  }, [listing]);
  const [appointmentDate, setAppointmentDate] = useState<Date>(
    earliestBookingDateAllowed || moment().toDate(),
  );
  const [appointmentTime, setAppointmentTime] = useState<number | null>(null);
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState<
    Record<number, true>
  >({});
  const [autoConfirmTimeSlots, setAutoConfirmTimeSlots] = useState<
    Record<number, true>
  >({});

  const offerDateMoment = useMemo(() => {
    const {
      offerSettings: { acceptedAnytime, offerDate: rawOfferDate },
    } = offerDetails!;
    const offerDate =
      acceptedAnytime && rawOfferDate ? null : new Date(rawOfferDate);
    if (!offerDate) return null;
    return moment(offerDate).startOf('date');
  }, [offerDetails]);

  const openHouseMoments = useMemo(() => {
    if (!listing!.org.settings.enableOpenHouses) return [];
    const openHouseTimeFromDates = listing!.openHouses.map(
      o => new Date(o.timeFrom),
    );
    return openHouseTimeFromDates.map(d => moment(d).startOf('date'));
  }, [listing]);

  const currentDateMoment = useMemo(() => moment().startOf('date'), []);
  const appointmentDateMoment = useMemo(
    () => moment(appointmentDate).startOf('date'),
    [appointmentDate],
  );

  const blockingAppointmentDateMap = useMemo(() => {
    const result: {
      [date: string]:
        | BookingTimeSelectContextType['appointmentTimeSlotInfoMap']
        | undefined;
    } = {};
    blockingAppointmentList.forEach(data => {
      result[data.date] ||= {};
      const timeMoment = moment(data.time);
      const timeSlot = timeMoment.hour() * 60 + timeMoment.minute();
      result[data.date]![timeSlot] ||= [];
      result[data.date]![timeSlot]!.push(data);
    });
    return result;
  }, [blockingAppointmentList]);

  const getDateStatesForDate: BookingTimeSelectContextType['getDateStatesForDate'] =
    useCallback(
      date => {
        const startOfDate = date.startOf('date');
        const isAppointmentDate = appointmentDateMoment.isSame(startOfDate);
        const isOfferDate =
          Boolean(offerDateMoment) && offerDateMoment!.isSame(startOfDate);
        const isOpenHouseDate = openHouseMoments.some(openHouseMoment =>
          openHouseMoment.isSame(startOfDate),
        );
        const isUnavailableAfterDate = unavailableAfterDate
          ? startOfDate.isSameOrAfter(unavailableAfterDate)
          : false;
        const isUnavailableDate = unavailableDateStrings.some(
          d => d === date.format('YYYY-MM-DD'),
        );
        const isPast = currentDateMoment.isAfter(startOfDate);
        const isBeforeActiveDate =
          listing!.status === 'Coming Soon' &&
          startOfDate.isBefore(earliestBookingDateAllowed);

        return {
          isAppointmentDate,
          isOfferDate,
          isOpenHouseDate,
          isUnavailableAfterDate,
          isUnavailableDate,
          isPast,
          isBeforeActiveDate,
          isToday: currentDateMoment.isSame(startOfDate),
        };
      },
      [
        appointmentDateMoment,
        currentDateMoment,
        earliestBookingDateAllowed,
        listing,
        offerDateMoment,
        openHouseMoments,
        unavailableAfterDate,
        unavailableDateStrings,
      ],
    );

  const isDisabledDate = useCallback(
    (date: Moment) => {
      const startOfDate = date.startOf('date');
      if (
        date.isBefore(moment().startOf('date')) &&
        !isAdminWithAccess &&
        !isListingAgent
      )
        return true;

      // Note that in the old datepicker unavailable after dates were clickable
      // because they would open a modal but that modal was broken lol
      const isUnavailableAfterDate = unavailableAfterDate
        ? startOfDate.isSameOrAfter(unavailableAfterDate)
        : false;
      if (isUnavailableAfterDate) return true;

      if (isAdminWithAccess || isListingAgent) return false;
      const isUnavailableDate = unavailableDateStrings.some(
        d => d === date.format('YYYY-MM-DD'),
      );
      return isUnavailableDate;
    },
    [
      isAdminWithAccess,
      isListingAgent,
      unavailableAfterDate,
      unavailableDateStrings,
    ],
  );

  const [selectedDateStates, setSelectedDateStates] = useState<
    BookingTimeSelectContextType['selectedDateStates']
  >(getDateStatesForDate(moment(appointmentDate).startOf('date')));
  const [isDurationPopoverOpen, setIsDurationPopoverOpen] = useState(false);

  const [selectedTimeStatus, setSelectedTimeStatus] = useState<
    null | 'Restricted' | 'AdvancedAutoconfirm'
  >(null);

  useEffect(() => {
    setAppointmentTime(null);
    setIsDurationPopoverOpen(false);
  }, [appointmentDate, setAppointmentTime]);

  useEffect(() => {
    if (appointmentTime === null) {
      setSelectedTimeStatus(null);
      return;
    }
    let isAdvAutoConfirm = true;
    for (let i = appointmentTime; i < appointmentTime + duration; i += 15) {
      if (unavailableTimeSlots[i]) {
        setSelectedTimeStatus('Restricted');
        return;
      }
      if (!autoConfirmTimeSlots[i]) {
        isAdvAutoConfirm = false;
      }
    }
    setSelectedTimeStatus(isAdvAutoConfirm ? 'AdvancedAutoconfirm' : null);
  }, [
    appointmentTime,
    duration,
    unavailableTimeSlots,
    autoConfirmTimeSlots,
    isAdminWithAccess,
    isListingAgent,
  ]);

  const fetchUnavailableTimeSlots = useCallback(
    async (date: Moment) => {
      const newUnavailableTimeSlots: typeof unavailableTimeSlots = {};
      const timeAvailability = await getTimeAvailability({
        listingId: listing!.id,
        dateStr: date.format('YYYY-MM-DD'),
      });
      Object.entries(timeAvailability).forEach(([timeString, available]) => {
        if (available) return;
        const timeSlot = getTimeSlotNumberFromString(timeString);
        newUnavailableTimeSlots[timeSlot] = true;
      });
      setUnavailableTimeSlots(newUnavailableTimeSlots);
    },
    [listing],
  );

  useEffect(() => {
    fetchUnavailableTimeSlots(appointmentDateMoment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setConditionalBuyerAgreementData({
      isRecordShowing: recordShowingData.isRecordShowing,
    });
  }, [recordShowingData.isRecordShowing, setConditionalBuyerAgreementData]);

  const onDateClick: BookingTimeSelectContextType['onDateClick'] = options => {
    const {
      isBeforeActiveDate,
      isOfferDate,
      isOpenHouseDate,
      isPast,
      isUnavailableAfterDate,
      isUnavailableDate,
      date,
      ignoreComingSoonWarning,
    } = options;
    const setTimeStates = async (skipFetch: boolean) => {
      if (!skipFetch) {
        await fetchUnavailableTimeSlots(date);

        if (
          !partialAppointmentConfig!.autoConfirm &&
          partialAppointmentConfig!.advAutoConfEnabled
        ) {
          const newAutoConfirmTimeSlots: typeof autoConfirmTimeSlots = {};
          const dayKey = moment(date)
            .format('ddd')
            .toUpperCase() as keyof NonNullable<
            typeof partialAppointmentConfig
          >['days'];
          const specialDayKey = moment(date).format('YYYY-MM-DD');
          const days = partialAppointmentConfig!.days[dayKey];
          const specialDays =
            partialAppointmentConfig!.specialTimes[specialDayKey];

          Object.entries(specialDays || days).forEach(
            ([timeSlotStr, value]) => {
              if (value !== 'AutoConfirm') return;
              const [hour, minute] = timeSlotStr.split(':');
              const timeSlot = +hour * 60 + +minute;
              newAutoConfirmTimeSlots[timeSlot] = true;
              // The timeslots on appointment config are segemented by 30mins so xx:15 and xx:45 timeslots use the xx:00 and xx:30 timeslot, respectively
              newAutoConfirmTimeSlots[timeSlot + 15] = true;
            },
          );
          setAutoConfirmTimeSlots(newAutoConfirmTimeSlots);
        }
      }

      setSelectedDateStates({
        isAppointmentDate: appointmentDateMoment.isSame(date),
        isBeforeActiveDate,
        isOfferDate,
        isOpenHouseDate,
        isPast,
        isToday: currentDateMoment.isSame(date),
        isUnavailableAfterDate,
        isUnavailableDate,
      });
      setAppointmentDate(date.toDate());
    };

    if (isPast || isBeforeActiveDate) {
      setUnavailableTimeSlots({});
      setAutoConfirmTimeSlots({});
    }
    if (isPast && recordShowingData.shouldShowWarning) {
      setRecordShowingData(prev => ({
        ...prev,
        isModalOpen: true,
        onOk: () => setTimeStates(true),
      }));
      return;
    }
    if (!isPast) {
      setRecordShowingData(prev => ({
        ...prev,
        isRecordShowing: false,
        isModalOpen: false,
        onOk: () => null,
      }));
    }
    if (!ignoreComingSoonWarning && isBeforeActiveDate) {
      setComingSoonWarningData({
        isModalOpen: true,
        onOk: () => {
          setComingSoonWarningData({ isModalOpen: false, onOk: () => null });
          onDateClick({ ...options, ignoreComingSoonWarning: true });
        },
      });
      return;
    }

    setTimeStates(
      isPast || (isBeforeActiveDate && !(isAdminWithAccess || isListingAgent)),
    );
  };

  const appointmentTimeSlotInfoMap =
    blockingAppointmentDateMap[appointmentDateMoment.format('YYYY-MM-DD')];

  return (
    <BookingTimeSelectContext.Provider
      value={{
        appointmentDate,
        setAppointmentDate,
        appointmentTime,
        setAppointmentTime,
        earliestBookingDateAllowed,
        recordShowingData,
        setRecordShowingData,
        restrictedTimeData,
        setRestrictedTimeData,
        comingSoonWarningData,
        setComingSoonWarningData,
        duration,
        setDuration,
        unavailableTimeSlots,
        autoConfirmTimeSlots,
        selectedDateStates,
        setSelectedDateStates,
        isDurationPopoverOpen,
        setIsDurationPopoverOpen,
        selectedTimeStatus,
        appointmentTimeSlotInfoMap,
        getDateStatesForDate,
        onDateClick,
        isDisabledDate,
      }}
    >
      {children}
    </BookingTimeSelectContext.Provider>
  );
};
export default BookingTimeSelectContext;
